/**
 * useAutoSave Hook - v1.5 Feature #1
 * Auto-saves wizard progress ONLY when data changes
 * Restores draft on page load
 */

import { useEffect, useRef, useState, useCallback } from 'react';
import { useAuth } from '../contexts/TestAuthContext';
import { api } from '../utils/api';

const AUTO_SAVE_INTERVAL = 60000; // 60 seconds (reduced token refresh frequency)
const BACKEND_URL = process.env.REACT_APP_BACKEND_URL || '';

interface AutoSaveOptions {
  step: number;
  data: any;
  enabled: boolean;
  reportId?: string | null; // If provided, update existing report instead of creating draft
}

interface DraftData {
  draft_id: string;
  step: number;
  data: any;
  last_modified: string;
}

export function useAutoSave({ step, data, enabled, reportId }: AutoSaveOptions) {
  const { getToken } = useAuth();
  // ✅ CRITICAL FIX: Initialize draftId from localStorage to prevent duplicate draft creation
  const [draftId, setDraftId] = useState<string | null>(() => {
    const saved = localStorage.getItem('current_draft_id');
    if (saved) {
      console.log('🔄 useAutoSave: Restored draft ID from localStorage:', saved);
    }
    return saved;
  });
  const [lastSaved, setLastSaved] = useState<Date | null>(null);
  const [isSaving, setIsSaving] = useState(false);
  const [saveError, setSaveError] = useState<string | null>(null);
  const intervalRef = useRef<NodeJS.Timeout | null>(null);
  
  // ✅ DIRTY TRACKING: Only save when user actually changes something
  const [isDirty, setIsDirty] = useState(false);
  const lastSavedDataRef = useRef<string | null>(null);
  
  // ✅ CRITICAL FIX: Use ref to always get latest data, avoiding stale closure issue
  const dataRef = useRef(data);
  const stepRef = useRef(step);
  const draftIdRef = useRef(draftId);
  
  // Keep refs in sync with props
  useEffect(() => {
    dataRef.current = data;
  }, [data]);
  
  useEffect(() => {
    stepRef.current = step;
  }, [step]);
  
  useEffect(() => {
    draftIdRef.current = draftId;
  }, [draftId]);
  
  // ✅ DIRTY TRACKING: Detect when data actually changes
  useEffect(() => {
    if (!data) return;
    
    // Create a hash of user-editable fields (exclude backend-computed data)
    const userEditableData = {
      client_name: data.client_name,
      claimant_name: data.claimant_name,
      business_address: data.business_address,
      zone: data.zone,
      business_type: data.business_type,
      event_date: data.event_date,
      event_end_date: data.event_end_date,
      claimant_email: data.claimant_email,
      law_firm: data.law_firm,
      // Don't include unified_upload_data, canonical_data, financial_data
      // as these are computed by the backend
    };
    
    const currentHash = JSON.stringify(userEditableData);
    
    if (lastSavedDataRef.current === null) {
      // First load - set baseline, don't mark dirty
      lastSavedDataRef.current = currentHash;
      console.log('📋 Auto-save: Set initial baseline (not dirty)');
    } else if (currentHash !== lastSavedDataRef.current) {
      // User changed something
      setIsDirty(true);
      console.log('✏️ Auto-save: Data changed, marking dirty');
    }
  }, [data]);

  // Auto-save function - uses refs to get latest data
  // Can also accept explicit data to avoid timing issues with state updates
  const saveDraft = useCallback(async (explicitData?: any, forcesSave: boolean = false) => {
    if (!enabled) return;
    
    // ✅ DIRTY TRACKING: Only save if user changed something (unless forced)
    if (!isDirty && !forcesSave && !explicitData) {
      console.log('⏭️ Skipping auto-save: No changes detected');
      return;
    }
    
    // Use explicit data if provided, otherwise use refs for latest values
    const currentData = explicitData || dataRef.current;
    const currentStep = stepRef.current;
    const currentDraftId = draftIdRef.current;
    
    console.log('💾 saveDraft called:', {
      hasExplicitData: !!explicitData,
      isDirty,
      forcesSave,
      tax1065Files: currentData?.tax1065Files?.length || 0,
      draftId: currentDraftId
    });

    // Don't save if we're on Step 1 with no data entered
    const hasData = currentData && (
      currentData.client_name || 
      currentData.claimant_name || 
      currentData.business_address ||
      currentData.zone ||
      currentData.business_type ||
      (currentData.financial_data && Object.keys(currentData.financial_data).length > 0)
    );

    if (currentStep === 1 && !hasData) {
      console.log('⏭️ Skipping auto-save: Step 1 with no data');
      return;
    }

    try {
      setIsSaving(true);
      setSaveError(null);

      // FIX: Add retry logic for token refresh errors
      let token;
      try {
        token = await getToken();
      } catch (tokenError) {
        console.warn('⚠️ Token refresh failed, retrying...', tokenError);
        // Wait 1 second and retry once
        await new Promise(resolve => setTimeout(resolve, 1000));
        try {
          token = await getToken();
        } catch (retryError) {
          console.error('❌ Token refresh failed after retry:', retryError);
          throw new Error('Authentication failed. Please refresh the page.');
        }
      }

      // If editing an existing report, use the update endpoint
      const endpoint = reportId 
        ? `${BACKEND_URL}/api/v1/reports/${reportId}/update`
        : `${BACKEND_URL}/api/v1/reports/drafts/save`;
      
      const method = reportId ? 'PUT' : 'POST';

      console.log(`💾 Auto-saving ${reportId ? 'report' : 'draft'}:`, reportId || currentDraftId || 'new');
      console.log(`🔴 CRITICAL DEBUG - tax1065Files:`, currentData.tax1065Files?.length || 0);
      console.log(`🔴 plFiles in data:`, currentData.plFiles?.length || 0);

      // CRITICAL FIX: Use XMLHttpRequest instead of fetch() to avoid Clerk rrweb-recorder
      // "Response.clone: Body has already been consumed" error
      const result = await new Promise<any>((resolve, reject) => {
        const xhr = new XMLHttpRequest();
        xhr.open(method, endpoint);
        xhr.setRequestHeader('Content-Type', 'application/json');
        xhr.setRequestHeader('Authorization', `Bearer ${token}`);
        
        xhr.onload = () => {
          if (xhr.status >= 200 && xhr.status < 300) {
            try {
              const responseData = JSON.parse(xhr.responseText);
              resolve(responseData);
            } catch (e) {
              reject(new Error('Failed to parse save response'));
            }
          } else {
            reject(new Error(`Failed to save: ${xhr.status} ${xhr.responseText}`));
          }
        };
        
        xhr.onerror = () => reject(new Error('Network error during save'));
        // 🔍 DEBUG: Log what data we're sending
        console.log('🔍 AUTO-SAVE PAYLOAD:', {
          step: currentStep,
          draft_id: currentDraftId,
          plFiles_length: currentData.plFiles?.length || 0,
          uploadedDocuments_length: currentData.uploadedDocuments?.length || 0,
          tax1065Files_length: currentData.tax1065Files?.length || 0,
          has_financial_data: !!currentData.financial_data,
          data_keys: Object.keys(currentData)
        });
        
        xhr.send(JSON.stringify({
          draft_id: currentDraftId,
          step: currentStep,
          data: currentData,
          last_modified: new Date().toISOString()
        }));
      });
      
      // Store draft_id for future saves
      if (result.draft_id && !currentDraftId) {
        setDraftId(result.draft_id);
        draftIdRef.current = result.draft_id;
        // Store in localStorage as backup
        localStorage.setItem('current_draft_id', result.draft_id);
      }

      setLastSaved(new Date());
      
      // ✅ Reset dirty flag and update baseline after successful save
      setIsDirty(false);
      const userEditableData = {
        client_name: currentData.client_name,
        claimant_name: currentData.claimant_name,
        business_address: currentData.business_address,
        zone: currentData.zone,
        business_type: currentData.business_type,
        event_date: currentData.event_date,
        event_end_date: currentData.event_end_date,
        claimant_email: currentData.claimant_email,
        law_firm: currentData.law_firm,
      };
      lastSavedDataRef.current = JSON.stringify(userEditableData);
      
      console.log('✅ Auto-save successful:', new Date().toLocaleTimeString());
    } catch (error) {
      console.error('❌ Error saving draft:', error);
      setSaveError('Failed to auto-save');
    } finally {
      setIsSaving(false);
    }
  }, [enabled, getToken, reportId, isDirty]); // Dependencies for useCallback

  // Restore draft on mount
  const restoreDraft = async (): Promise<DraftData | null> => {
    try {
      const draft = await api.get('/api/v1/reports/drafts/latest', { showErrorToast: false });
      
      if (draft && draft.draft_id) {
        setDraftId(draft.draft_id);
        localStorage.setItem('current_draft_id', draft.draft_id);
        return draft;
      }

      return null;
    } catch (error) {
      console.error('Error restoring draft:', error);
      return null;
    }
  };

  // Delete draft (call after successful submission)
  const deleteDraft = async () => {
    if (!draftId) return;

    try {
      await api.delete(`/api/v1/reports/drafts/${draftId}`, { showErrorToast: false });
      setDraftId(null);
      localStorage.removeItem('current_draft_id');
    } catch (error) {
      console.error('Error deleting draft:', error);
    }
  };

  // Set up auto-save interval
  useEffect(() => {
    if (!enabled) {
      if (intervalRef.current) {
        clearInterval(intervalRef.current);
      }
      return;
    }

    // Save immediately on data change (debounced by interval)
    intervalRef.current = setInterval(() => {
      saveDraft();
    }, AUTO_SAVE_INTERVAL);

    // Cleanup
    return () => {
      if (intervalRef.current) {
        clearInterval(intervalRef.current);
      }
    };
  }, [step, data, enabled, draftId]);

  // Manual save function
  const saveNow = () => {
    saveDraft();
  };

  return {
    draftId,
    lastSaved,
    isSaving,
    saveError,
    saveNow,
    restoreDraft,
    deleteDraft
  };
}
